import { HugeiconsIcon } from "@hugeicons/react";
import { FC, useCallback } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { Cancel01Icon, RefreshIcon } from "@hugeicons-pro/core-stroke-rounded";
import { formatToCurrency } from "shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { logger } from "logger";

const rerunErrorMessageMap: Record<string, string> = {
  incomplete_verification:
    "The current identity verification has to finish before it can be re-run",
  archived_verification:
    "This identity verification report has already been replaced by a newer one",
  identity_verification_not_found:
    "This applicant has no identity verification report to re-run",
  missing_requirements:
    "Identity verification can't be re-run once the application is on hold, canceled, or decisioned",
  not_authorized_to_rerun_idv:
    "You don't have permission to re-run the identity verification report"
};

interface IdentityVerificationRerunModalProps {
  opened: boolean;
  onClose: () => void;
  applicantId?: number;
  applicationId?: number;
  rerunFee?: {
    cents: number;
    currency: string;
  };
  refetchIdv?: () => void;
}

export const IdentityVerificationRerunModal: FC<
  IdentityVerificationRerunModalProps
> = ({ opened, onClose, rerunFee, applicantId, applicationId, refetchIdv }) => {
  const queryClient = useQueryClient();

  const refetchApplication = useCallback(() => {
    queryClient
      .refetchQueries({
        queryKey: ["APPLICATION", String(applicationId)]
      })
      .catch((error) => {
        logger.error("Failed to invalidate queries", error);
      });
  }, [applicationId, queryClient]);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/rerun_identity_verification`
      ),
    onSuccess: () => {
      refetchApplication();
      refetchIdv?.();
      onClose();
    },
    onError: (error) => {
      // Every rejection describes report state the partner is seeing out of date, so
      // refresh it rather than leaving the confirmation open over stale data.
      refetchIdv?.();
      refetchApplication();
      onClose();

      const errorMessage = getErrorMessage(error);

      toast.error(
        rerunErrorMessageMap[errorMessage] ||
          `Re-run identity verification report failed: ${errorMessage}`
      );
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="p-6 max-w-[400px] w-full"
    >
      <div className="space-y-4 relative">
        <div className="flex items-center justify-center rounded-full bg-alpha-40 p-4 mb-6 mx-auto w-16 h-16 relative">
          <HugeiconsIcon icon={RefreshIcon} size={32} />
        </div>
        <IconButton
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
          className="shrink-0 text-content-200 absolute -top-4 right-0"
          onClick={onClose}
          size={IconButtonSize.LG}
        />
        <h1 className="text-xl font-bold text-content-1000 text-center">
          Re-run identity verification report
        </h1>
        <p className="text-base text-content-500 text-center">
          Boom will send an identity verification request to the applicant via
          email and SMS
        </p>
        <div className="p-4 bg-primary-20 text-primary-600 rounded-lg">
          <ul className="list-disc pl-5 space-y-2">
            {rerunFee ? (
              <li className="text-sm font-medium">
                {`You will be charged an additional fee of ${formatToCurrency({
                  cents: rerunFee.cents,
                  currency: rerunFee.currency
                })} once the verification is complete`}
              </li>
            ) : null}
            <li className="text-sm font-medium">
              You&apos;ll receive an email once new identity data is received
            </li>
            <li className="text-sm font-medium">
              A new identity verification report will be generated
            </li>
          </ul>
        </div>
        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.PRIMARY}
          className="w-full"
          loading={isPending}
          disabled={isPending}
          onClick={() => mutate()}
        >
          Confirm and send SMS
        </Button>
      </div>
    </Modal>
  );
};
